/**
 * <cart-recommendations>
 * Prodotti suggeriti dentro il cart drawer di Horizon (4.1.x).
 *
 * Come si costruisce il bacino dei suggeriti:
 * - come "semi" usa le righe piu' costose del carrello (max MAX_SEEDS), non solo la prima:
 *   cosi' aggiungendo un prodotto entrano in gioco anche i suoi suggeriti;
 * - per ogni seme unisce i complementari (curati a mano in Search & Discovery) e i correlati
 *   (algoritmo Shopify), in quest'ordine: la curatela resta in cima, i correlati riempiono.
 *   Shopify restituisce ~3 complementari e ~10 correlati per prodotto: usarne uno solo
 *   lasciava la vetrina a 3 card;
 * - esclude i prodotti gia' nel carrello e i duplicati, mostra le prime `data-max` card e
 *   tiene il resto come riserva.
 *
 * Quando l'utente aggiunge un suggerito, la card viene subito rimpiazzata dalla successiva
 * della riserva invece di lasciare un buco.
 *
 * IMPORTANTE — TRACCIAMENTO: l'aggiunta al carrello passa da CartLinesUpdateEvent, lo stesso
 * evento standard che usa il bottone "Aggiungi al carrello" del tema. E' quello che alimenta i
 * web pixel (Meta incluso). Non sostituirlo con una fetch "muta": gli AddToCart smetterebbero
 * di essere tracciati.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/** Quante righe del carrello usare come base per i suggerimenti. */
const MAX_SEEDS = 3;

class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.intent = this.getAttribute('data-intent') || 'complementary';
    this.limit = parseInt(this.getAttribute('data-limit') || '10', 10);
    this.maxShow = parseInt(this.getAttribute('data-max') || '3', 10);
    this.sectionName = this.getAttribute('data-section') || 'cart-recommendations';
    this.recUrl = this.getAttribute('data-recommendations-url') || '/recommendations/products';
    this._busy = false;
    this._dialog = null;
    this._dialogObserver = null;
    this._cache = new Map();
    this._pool = [];
    this._onCartLinesUpdate = this._onCartLinesUpdate.bind(this);
  }

  connectedCallback() {
    // Gira solo dentro il drawer, non nella pagina carrello.
    if (!this.closest('cart-drawer-component')) return;

    document.addEventListener(StandardEvents.cartLinesUpdate, this._onCartLinesUpdate);

    // Frecce di scorrimento (desktop).
    this._list = this.querySelector('[data-cart-recs-list]');
    this._viewport = this.querySelector('.cart-recs__viewport');
    this._prevBtn = this.querySelector('[data-cart-recs-prev]');
    this._nextBtn = this.querySelector('[data-cart-recs-next]');
    if (this._prevBtn) this._prevBtn.addEventListener('click', () => this._scrollByCards(-1));
    if (this._nextBtn) this._nextBtn.addEventListener('click', () => this._scrollByCards(1));
    if (this._list) this._list.addEventListener('scroll', () => this._updateNav(), { passive: true });

    // Ricarica ogni volta che il drawer (dialog) viene aperto.
    this._dialog = this.closest('dialog');
    if (this._dialog) {
      this._dialogObserver = new MutationObserver(() => {
        if (this._dialog.hasAttribute('open') || this._dialog.open) {
          this.refresh();
        }
      });
      this._dialogObserver.observe(this._dialog, { attributes: true, attributeFilter: ['open'] });
    }

    this.refresh();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this._onCartLinesUpdate);
    if (this._dialogObserver) {
      this._dialogObserver.disconnect();
      this._dialogObserver = null;
    }
  }

  _onCartLinesUpdate(event) {
    // Aspetta che il tema abbia completato il morph della sezione drawer,
    // poi ricalcola i suggeriti sulla base del carrello aggiornato.
    const run = () => setTimeout(() => this.refresh(), 150);
    if (event && event.promise) {
      event.promise.then(run).catch(() => {});
    } else {
      run();
    }
  }

  /** Gli intent da interrogare, in ordine di priorita'. */
  get _intents() {
    return this.intent === 'related' ? ['related'] : [this.intent, 'related'];
  }

  async refresh() {
    if (this._busy || !this.isConnected) return;
    this._busy = true;
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

      if (!cart || !cart.items || cart.items.length === 0) {
        this._hide();
        return;
      }

      this._pool = await this._buildPool(cart);
      this._render();
    } catch (err) {
      console.warn('[cart-recommendations] refresh error:', err);
      this._hide();
    } finally {
      this._busy = false;
    }
  }

  /**
   * Lista ordinata dei candidati: complementari di tutti i semi, poi correlati di tutti i semi,
   * senza duplicati e senza i prodotti gia' nel carrello.
   */
  async _buildPool(cart) {
    const seeds = [...cart.items]
      .sort((a, b) => b.final_line_price - a.final_line_price)
      .map((item) => item.product_id)
      .filter((id, index, all) => all.indexOf(id) === index)
      .slice(0, MAX_SEEDS);

    const requests = [];
    for (const intent of this._intents) {
      for (const seed of seeds) requests.push(this._fetchIntent(seed, intent));
    }

    const groups = await Promise.all(requests);
    const inCart = new Set(cart.items.map((item) => String(item.product_id)));
    const seen = new Set();
    const pool = [];

    for (const group of groups) {
      for (const card of group) {
        const productId = card.getAttribute('data-product-id');
        if (!productId || inCart.has(productId) || seen.has(productId)) continue;
        seen.add(productId);
        pool.push(card);
      }
    }

    return pool;
  }

  /**
   * Card per un seme e un intent. Il risultato non dipende dal carrello (il filtro sui prodotti
   * gia' presenti avviene dopo), quindi si puo' tenere in cache per tutta la vita del drawer.
   */
  async _fetchIntent(productId, intent) {
    const key = `${productId}:${intent}`;
    const cached = this._cache.get(key);
    if (cached) return cached;

    const url =
      `${this.recUrl}?product_id=${productId}` +
      `&intent=${encodeURIComponent(intent)}` +
      `&limit=${this.limit}` +
      `&section_id=${encodeURIComponent(this.sectionName)}`;

    let cards = [];
    try {
      const res = await fetch(url);
      if (res.ok) {
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const wrap = doc.querySelector('[data-cart-recs-cards]');
        if (wrap) cards = Array.from(wrap.querySelectorAll('.cart-recs__card'));
      }
    } catch (err) {
      console.warn('[cart-recommendations] fetch error:', err);
    }

    this._cache.set(key, cards);
    return cards;
  }

  _render() {
    const list = this.querySelector('[data-cart-recs-list]');
    if (!list || this._pool.length === 0) {
      this._hide();
      return;
    }

    const scrollLeft = list.scrollLeft;
    list.replaceChildren(...this._pool.slice(0, this.maxShow).map((card) => card.cloneNode(true)));
    list.scrollLeft = scrollLeft;

    this._bindAddButtons();
    this._show();
    this._updateNav();
  }

  _scrollByCards(dir) {
    if (!this._list) return;
    const card = this._list.querySelector('.cart-recs__card');
    const step = card ? (card.getBoundingClientRect().width + 12) * 2 : 240;
    this._list.scrollBy({ left: dir * step, behavior: 'smooth' });
  }

  _updateNav() {
    if (!this._list || !this._viewport) return;
    const scrollable = this._list.scrollWidth - this._list.clientWidth > 2;
    if (scrollable) {
      this._viewport.setAttribute('data-scrollable', '');
    } else {
      this._viewport.removeAttribute('data-scrollable');
    }
    const atStart = this._list.scrollLeft <= 1;
    const atEnd = this._list.scrollLeft + this._list.clientWidth >= this._list.scrollWidth - 1;
    if (this._prevBtn) this._prevBtn.disabled = atStart;
    if (this._nextBtn) this._nextBtn.disabled = atEnd;
  }

  _bindAddButtons() {
    this.querySelectorAll('[data-cart-recs-add]').forEach((btn) => {
      btn.addEventListener('click', (e) => this._addToCart(e.currentTarget));
    });
  }

  async _addToCart(btn) {
    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId || btn.classList.contains('is-loading')) return;

    const productId = btn.closest('.cart-recs__card')?.getAttribute('data-product-id');

    btn.classList.add('is-loading');
    btn.disabled = true;

    // Sezioni del carrello (drawer) da far ri-renderizzare a Shopify.
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    });

    const body = { items: [{ id: Number(variantId), quantity: 1 }] };
    if (sectionIds.size) {
      body.sections = Array.from(sectionIds).join(',');
      body.sections_url = window.location.pathname;
    }

    // Stesso flusso del bottone nativo: evento standard con promise differita.
    // I web pixel (Meta) ascoltano questo evento — vedi nota in testa al file.
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw data;

      // Carrello aggiornato (totali, barra spedizione, contatore).
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          source: 'cart-recommendations',
          variantId: variantId,
          sections: data.sections || undefined,
          didError: false,
        },
      });

      // Rimpiazza subito la card appena aggiunta con la successiva della riserva.
      if (productId) {
        this._pool = this._pool.filter((card) => card.getAttribute('data-product-id') !== productId);
        this._render();
      }
    } catch (err) {
      console.warn('[cart-recommendations] add error:', err);
      deferred.reject(err);
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  }

  _show() {
    this.removeAttribute('hidden');
  }

  _hide() {
    this.setAttribute('hidden', '');
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
